import {FormEventHandler} from 'react';
import {Head, Link, useForm} from '@inertiajs/react';
import InputError from '@/components/ui/form/input-error';
import InputLabel from '@/components/ui/form/input-label';
import Checkbox from '@/components/ui/form/checkbox';
import AuthLayout from '@/layouts/auth-layout';

interface LoginProps {
  status?: string;
  canResetPassword: boolean;
}

export default function Login({status, canResetPassword}: LoginProps) {
  const {data, setData, post, processing, errors, reset} = useForm({
    email: '',
    password: '',
    remember: false,
  });

  const submit: FormEventHandler = (event) => {
    event.preventDefault();

    post(route('login'), {
      onFinish: () => reset('password'),
    });
  };

  const hasFormErrors = Boolean(errors.email || errors.password);

  return (
    <AuthLayout
      title="Sign in to your account"
      subtitle="Use your existing credentials to continue."
    >
      <Head title="Log in"/>
      <form className="space-y-5" onSubmit={submit}>
                {status ? (
                  <div className="alert alert-info text-sm" role="status">
                    <span>{status}</span>
                  </div>
                ) : null}

                {hasFormErrors ? (
                  <div className="alert alert-error text-sm" role="alert">
                    <span>We couldn’t sign you in. Double-check your credentials and try again.</span>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <InputLabel htmlFor="email" value="Email"/>
                  <label className="input input-bordered flex h-12 items-center gap-2 focus-within:outline focus-within:outline-2 focus-within:outline-primary/40">
                    <svg
                      aria-hidden="true"
                      className="size-4 shrink-0 text-base-content/60"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.8"
                      viewBox="0 0 24 24"
                    >
                      <path d="M4 6h16v12H4z"/>
                      <path d="m22 7-10 7L2 7"/>
                    </svg>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={data.email}
                      className="h-full w-full border-0 bg-transparent px-0 text-base-content focus:outline-none focus:ring-0"
                      autoComplete="username"
                      autoFocus={true}
                      onChange={(event) => setData('email', event.target.value)}
                    />
                  </label>
                  <InputError message={errors.email} className="mt-1"/>
                </div>

                <div className="space-y-2">
                  <InputLabel htmlFor="password" value="Password"/>
                  <label className="input input-bordered flex h-12 items-center gap-2 focus-within:outline focus-within:outline-2 focus-within:outline-primary/40">
                    <svg
                      aria-hidden="true"
                      className="size-4 shrink-0 text-base-content/60"
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.8"
                      viewBox="0 0 24 24"
                    >
                      <rect height="11" rx="2" width="14" x="5" y="11"/>
                      <path d="M8 11V8a4 4 0 0 1 8 0v3"/>
                    </svg>
                    <input
                      id="password"
                      type="password"
                      name="password"
                      value={data.password}
                      className="h-full w-full border-0 bg-transparent text-base-content focus:outline-none focus:ring-0"
                      autoComplete="current-password"
                      onChange={(event) => setData('password', event.target.value)}
                    />
                  </label>
                  <InputError message={errors.password} className="mt-1"/>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <label className="label cursor-pointer justify-start gap-2 p-0">
                    <Checkbox
                      name="remember"
                      checked={data.remember}
                      onChange={(event) => setData('remember', event.target.checked)}
                    />
                    <span className="label-text text-sm text-base-content/80">Remember me</span>
                  </label>

                  {canResetPassword ? (
                    <Link href={route('password.request')} className="text-sm font-medium text-primary hover:underline focus-visible:underline">
                      Forgot password?
                    </Link>
                  ) : null}
                </div>

                <button
                  className="btn btn-primary h-12 w-full"
                  disabled={processing}
                  type="submit"
                >
                  {processing ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="loading loading-spinner loading-sm" aria-hidden="true"/>
                      Signing in…
                    </span>
                  ) : 'Log in'}
                </button>
      </form>
    </AuthLayout>
  );
}
